import React, { useEffect, useRef } from 'react';

// WebGL cinematic renderer for the Gallery story mode.
// three.js is imported dynamically so the ~150KB library is only fetched
// the first time a user enters cinematic mode, not on app load.

const VERT = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 1.0);
}
`;

const FRAG = `
precision highp float;
varying vec2 vUv;
uniform sampler2D uTexA;
uniform sampler2D uTexB;
uniform float uProgress;
uniform float uTime;
uniform float uStartA;
uniform float uStartB;
uniform float uSeedA;
uniform float uSeedB;
uniform vec2 uRes;
uniform vec2 uResA;
uniform vec2 uResB;
uniform int uFitMode;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x),
    f.y
  );
}

// background-size: cover, in shader form
vec2 coverUv(vec2 uv, vec2 imgRes) {
  float ca = uRes.x / max(uRes.y, 1.0);
  float ia = imgRes.x / max(imgRes.y, 1.0);
  vec2 s = ca > ia ? vec2(1.0, ia / ca) : vec2(ca / ia, 1.0);
  return (uv - 0.5) * s + 0.5;
}

// background-size: contain, in shader form
vec2 containUv(vec2 uv, vec2 imgRes) {
  float ca = uRes.x / max(uRes.y, 1.0);
  float ia = imgRes.x / max(imgRes.y, 1.0);
  vec2 s = ca > ia ? vec2(ca / ia, 1.0) : vec2(1.0, ia / ca);
  return (uv - 0.5) * s + 0.5;
}

// gentle push-in with a per-slide drift — keep zoom barely perceptible
vec2 kenBurns(vec2 uv, float t, float seed) {
  float k = min(t / 7.0, 1.0);
  float zoom = 1.012 + 0.018 * k;
  vec2 drift = vec2(cos(seed * 6.2831), sin(seed * 6.2831)) * 0.007 * k;
  return (uv - 0.5) / zoom + 0.5 + drift;
}

void main() {
  vec2 baseUvA = uFitMode == 1 ? containUv(vUv, uResA) : coverUv(vUv, uResA);
  vec2 baseUvB = uFitMode == 1 ? containUv(vUv, uResB) : coverUv(vUv, uResB);

  vec2 uvA = kenBurns(baseUvA, uTime - uStartA, uSeedA);
  vec2 uvB = kenBurns(baseUvB, uTime - uStartB, uSeedB);

  float p = uProgress;
  float swell = sin(p * 3.14159265);

  // organic displacement that peaks mid-transition
  vec2 disp = vec2(
    noise(vUv * 5.0 + uTime * 0.15) - 0.5,
    noise(vUv * 5.0 + 31.7 - uTime * 0.12) - 0.5
  ) * swell * 0.022;

  vec4 colA = texture2D(uTexA, clamp(uvA + disp * p, 0.001, 0.999));
  if (uFitMode == 1 && (uvA.x < 0.0 || uvA.x > 1.0 || uvA.y < 0.0 || uvA.y > 1.0)) {
    colA = vec4(0.0, 0.0, 0.0, 1.0);
  }

  vec4 colB = texture2D(uTexB, clamp(uvB - disp * (1.0 - p), 0.001, 0.999));
  if (uFitMode == 1 && (uvB.x < 0.0 || uvB.x > 1.0 || uvB.y < 0.0 || uvB.y > 1.0)) {
    colB = vec4(0.0, 0.0, 0.0, 1.0);
  }

  // noise-guided dissolve: edge=1 shows A, edge=0 shows B
  float n = noise(vUv * 7.0 + uSeedB * 13.0);
  float lo = p * 1.5 - 0.25;
  float edge = smoothstep(lo, lo + 0.25, n);
  vec4 col = mix(colB, colA, edge);

  // vignette
  float d = distance(vUv, vec2(0.5));
  col.rgb *= 1.0 - smoothstep(0.45, 0.95, d) * 0.55;

  // animated film grain
  float g = hash(vUv * uRes * 0.5 + fract(uTime) * 100.0) - 0.5;
  col.rgb += g * 0.045;

  // subtle warm cinematic grade
  col.rgb = pow(col.rgb, vec3(0.98, 1.0, 1.04));

  gl_FragColor = vec4(col.rgb, 1.0);
}
`;

const TRANSITION_SECS = 1.25;
const PARTICLE_COUNT = 110;

export default function CinematicPlayer({ src, fitMode = 'contain', onFallback }) {
  const mountRef = useRef(null);
  const apiRef = useRef(null);
  const latestSrcRef = useRef(src);
  const uniformsRef = useRef(null);

  latestSrcRef.current = src;

  useEffect(() => {
    let disposed = false;
    const mount = mountRef.current;
    const cleanupFns = [];

    (async () => {
      let THREE;
      try {
        THREE = await import('three');
      } catch {
        if (!disposed) onFallback?.();
        return;
      }
      if (disposed || !mount) return;

      let renderer;
      try {
        renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      } catch {
        if (!disposed) onFallback?.();
        return;
      }

      renderer.setSize(mount.clientWidth, mount.clientHeight);
      Object.assign(renderer.domElement.style, { position: 'absolute', inset: '0', width: '100%', height: '100%' });
      mount.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 10);
      camera.position.z = 1;

      const placeholder = new THREE.DataTexture(new Uint8Array([5, 5, 5, 255]), 1, 1);
      placeholder.needsUpdate = true;

      const uniforms = {
        uTexA: { value: placeholder },
        uTexB: { value: placeholder },
        uProgress: { value: 0 },
        uTime: { value: 0 },
        uStartA: { value: 0 },
        uStartB: { value: 0 },
        uSeedA: { value: Math.random() },
        uSeedB: { value: Math.random() },
        uFitMode: { value: fitMode === 'contain' ? 1 : 0 },
        uRes: { value: new THREE.Vector2(mount.clientWidth, mount.clientHeight) },
        uResA: { value: new THREE.Vector2(1, 1) },
        uResB: { value: new THREE.Vector2(1, 1) },
      };
      uniformsRef.current = uniforms;

      const quad = new THREE.Mesh(
        new THREE.PlaneGeometry(2, 2),
        new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, uniforms, depthTest: false })
      );
      scene.add(quad);

      // floating dust particles for cinema atmosphere
      const positions = new Float32Array(PARTICLE_COUNT * 3);
      const speeds = new Float32Array(PARTICLE_COUNT);
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        positions[i * 3] = Math.random() * 2 - 1;
        positions[i * 3 + 1] = Math.random() * 2 - 1;
        positions[i * 3 + 2] = 0.5;
        speeds[i] = 0.015 + Math.random() * 0.05;
      }
      const particleGeo = new THREE.BufferGeometry();
      particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));

      const spriteCanvas = document.createElement('canvas');
      spriteCanvas.width = spriteCanvas.height = 32;
      const ctx2d = spriteCanvas.getContext('2d');
      const grad = ctx2d.createRadialGradient(16, 16, 0, 16, 16, 16);
      grad.addColorStop(0, 'rgba(255,255,255,0.9)');
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx2d.fillStyle = grad;
      ctx2d.fillRect(0, 0, 32, 32);
      const sprite = new THREE.CanvasTexture(spriteCanvas);

      const particles = new THREE.Points(particleGeo, new THREE.PointsMaterial({
        size: 3,
        sizeAttenuation: false,
        map: sprite,
        transparent: true,
        opacity: 0.3,
        depthTest: false,
        blending: THREE.AdditiveBlending,
      }));
      scene.add(particles);

      const loader = new THREE.TextureLoader();
      const clock = new THREE.Clock();
      let loadToken = 0;
      let transStart = null;

      apiRef.current = {
        setImage(srcUrl) {
          const token = ++loadToken;
          loader.load(srcUrl, (tex) => {
            if (disposed || token !== loadToken) { tex.dispose(); return; }
            tex.colorSpace = THREE.SRGBColorSpace;
            const w = tex.image?.naturalWidth || tex.image?.width || 1;
            const h = tex.image?.naturalHeight || tex.image?.height || 1;
            if (uniforms.uTexA.value === placeholder) {
              uniforms.uTexA.value = tex;
              uniforms.uResA.value.set(w, h);
              uniforms.uStartA.value = clock.getElapsedTime();
              uniforms.uSeedA.value = Math.random();
              uniforms.uProgress.value = 0;
            } else {
              if (uniforms.uTexB.value !== placeholder) uniforms.uTexB.value.dispose();
              uniforms.uTexB.value = tex;
              uniforms.uResB.value.set(w, h);
              uniforms.uStartB.value = clock.getElapsedTime();
              uniforms.uSeedB.value = Math.random();
              transStart = clock.getElapsedTime();
            }
          }, undefined, () => {});
        },
      };

      let raf;
      const animate = () => {
        raf = requestAnimationFrame(animate);
        const t = clock.getElapsedTime();
        uniforms.uTime.value = t;

        if (transStart !== null) {
          const p = Math.min((t - transStart) / TRANSITION_SECS, 1);
          // easeInOutCubic
          uniforms.uProgress.value = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
          if (p >= 1) {
            const old = uniforms.uTexA.value;
            uniforms.uTexA.value = uniforms.uTexB.value;
            uniforms.uResA.value.copy(uniforms.uResB.value);
            uniforms.uStartA.value = uniforms.uStartB.value;
            uniforms.uSeedA.value = uniforms.uSeedB.value;
            uniforms.uTexB.value = placeholder;
            uniforms.uProgress.value = 0;
            transStart = null;
            if (old !== placeholder) old.dispose();
          }
        }

        const arr = particleGeo.attributes.position.array;
        for (let i = 0; i < PARTICLE_COUNT; i++) {
          arr[i * 3 + 1] += speeds[i] * 0.008;
          arr[i * 3] += Math.sin(t * 0.3 + i) * 0.0002;
          if (arr[i * 3 + 1] > 1.05) arr[i * 3 + 1] = -1.05;
        }
        particleGeo.attributes.position.needsUpdate = true;

        renderer.render(scene, camera);
      };
      animate();

      const onResize = () => {
        renderer.setSize(mount.clientWidth, mount.clientHeight);
        uniforms.uRes.value.set(mount.clientWidth, mount.clientHeight);
      };
      window.addEventListener('resize', onResize);

      cleanupFns.push(() => {
        cancelAnimationFrame(raf);
        window.removeEventListener('resize', onResize);
        quad.geometry.dispose();
        quad.material.dispose();
        particleGeo.dispose();
        particles.material.dispose();
        sprite.dispose();
        [uniforms.uTexA.value, uniforms.uTexB.value].forEach((tx) => {
          if (tx && tx !== placeholder) tx.dispose();
        });
        placeholder.dispose();
        renderer.dispose();
        renderer.domElement.parentNode?.removeChild(renderer.domElement);
      });

      // show whatever slide is current by the time init finished
      if (latestSrcRef.current) apiRef.current.setImage(latestSrcRef.current);
    })();

    return () => {
      disposed = true;
      cleanupFns.forEach((fn) => fn());
      apiRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (src) apiRef.current?.setImage(src);
  }, [src]);

  useEffect(() => {
    if (uniformsRef.current) {
      uniformsRef.current.uFitMode.value = fitMode === 'contain' ? 1 : 0;
    }
  }, [fitMode]);

  return <div ref={mountRef} style={{ position: 'absolute', inset: 0, overflow: 'hidden' }} />;
}
